/* eslint-disable react-hooks/exhaustive-deps */
/**
 * UPLOAD TYPE SEPARATION:
 *
 * This file handles PAYROLL uploads with BOTH anti-virus AND decryption checks.
 *
 * - Payroll: shouldEnablePayrollReload() + shouldPoll(av, decryption) + isFileValid(av, decryption)
 * - Disbursement Report: usePaymentOpsUploadFile hook - shouldPoll(av only)
 * - Prefund: useUploadPrefund hook - shouldPoll(av only)
 *
 * Payroll files can be encrypted and require decryption, while disbursement
 * reports and prefund files do not.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Divider,
  Flex,
  SimpleGrid,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useDropzone } from "react-dropzone";
import DatePicker from "components/DatePicker";
import {
  useGetRequestDetailsQuery,
  useUploadDataMutation,
} from "redux/services/dataLoader";
import { useGetValidationResultsQuery } from "redux/services/cryptography";
import {
  AntiVirusStatuses,
  DataLoaderDataSets,
  DataLoaderUploadStatuses,
  DecryptionStatuses,
  ENTITY_ID,
  PaymentMethod,
  Roles,
} from "common";
import { APPROVAL_CATEGORY_CODES } from "constants/approvalCategoryCodes";
import { isFileValid, shouldPoll, getValidationStatusWithError, ValidationStatuses } from "utils/statusHelpers";
import {
  useGetOrganizationQuery,
  useGetProfileQuery,
} from "redux/services/membership";
import {
  useCreateDocumentMutation,
  useDeleteDocumentMutation,
  useGetDocumentMutation,
} from "redux/services/documentRepository";
import { cloneDeep } from "lodash";
import { DisbursementDataType, PaymentKey } from "./config";
import {
  ArrowForwardIcon,
  CheckIcon,
  CloseIcon,
} from "@chakra-ui/icons";
import { useCreateProcessesMutation } from "redux/services/approvalProcessor";
import {
  getOrganisationMembership,
  dateFormat,
  FORMATS,
  getMimeType,
} from "utils";
import env from "env";
import { CreateDocumentResponse, DeleteDocumentRequest } from "redux/services/documentRepository/types";

import {
  validationFileSize,
  getVerifyFileName,
  verifyUploadBeforeTenAMToday,
} from "./Payment.data";

import { useOrgIdWithRel } from "hooks/useOrgIdWithRel";
import {
  handleRemoveFile,
  resetRequestDetailsInterval,
  resetRequestDocumentInterval,
  resetDualPollingInterval,
  setNewDisbursementData,
} from "pages/Payment/Payment.helper";
import { useRoles } from "hooks/useRoles";
import { FileDetailsRequest } from "redux/services/dataLoader/types";
import AdditionalData from "containers/AdditionalData";
import useUploadPrefund from "hooks/useUploadPrefund";
import PayrollAdditionalData from "containers/PayrollAdditionalData";

const TEMPLATE_FILE_URL = env.api.payrollTemplateFileUrl;

// APEX-873: Extract payrollDocumentName logic for testability - ensures SonarQ coverage
export const getPayrollDocumentName = (dataFilePayroll: any): string | undefined => {
  return dataFilePayroll?.data?.originalDocumentId;
};

type PrefundDocumentData = {
  antiVirusStatus?: string;
  decryptionStatus?: string;
  isEncrypted?: boolean;
  [key: string]: any;
};

type ProofPrefundFile = {
  errors?: string[];
  [key: string]: any;
};

export const isPrefundFileValid = (
  tradingName: string | undefined,
  proofPrefundFile?: ProofPrefundFile,
  prefundDocumentDetail?: { data?: PrefundDocumentData }
): boolean => {
  const documentData = prefundDocumentDetail?.data;

  const statusesValid = isFileValid(
    documentData?.antiVirusStatus
  );

  const hasFileErrors =
    !!proofPrefundFile?.errors && proofPrefundFile.errors.length > 0;

  if (tradingName === PaymentMethod.NORMAL_PAY) {
    if (!proofPrefundFile) return false;
    return statusesValid && !hasFileErrors;
  }

  if (!proofPrefundFile) {
    return true;
  }

  return statusesValid && !hasFileErrors;
};

/**
 * PAYROLL-SPECIFIC: Check if request reload should be enabled for PAYROLL uploads
 * Includes anti-virus, decryption, AND validation status checks (APEX-1151)
 *
 * For other upload types:
 * - Disbursement Report: Uses usePaymentOpsUploadFile hook (no decryption)
 * - Prefund: Uses useUploadPrefund hook (no decryption)
 *
 * @param payrollDocumentDetail - Document details with antiVirusStatus and decryptionStatus
 * @param dataFilePayroll - File data with upload status
 * @param validationStatus - Validation status from file-validation-service (APEX-1151)
 * @param isValidationEnabled - Whether validation feature is enabled (APEX-1151)
 * @returns true if should stop loading and enable reload button
 */
export const shouldEnablePayrollReload = (
  payrollDocumentDetail?: { data?: any },
  dataFilePayroll?: { data?: any },
  validationStatus?: string,
  isValidationEnabled?: boolean
): boolean => {
  if (!payrollDocumentDetail || !dataFilePayroll) return false;

  const uploadStatus = dataFilePayroll?.data?.status;
  const antiVirusStatus = payrollDocumentDetail?.data?.antiVirusStatus;
  const decryptionStatus = payrollDocumentDetail?.data?.decryptionStatus;

  // Stop polling immediately if decryption failed (don't wait for anti-virus to finish)
  if (decryptionStatus === DecryptionStatuses.FAILED) {
    return true; // Stop loading and show error
  }
  // Check if anti-virus is scanning (status field contains SCANNING during scan)
  if (uploadStatus === AntiVirusStatuses.SCANNING) {
    return false;
  }

  // Check if decryption is in progress (status field contains decryption statuses during decryption)
  if (
    uploadStatus === DecryptionStatuses.REQUESTED ||
    uploadStatus === DecryptionStatuses.IN_PROGRESS
  ) {
    return false;
  }


  // Check if still processing upload
  if (
    [DataLoaderUploadStatuses.PROCESSING, DataLoaderUploadStatuses.UPLOADED].includes(
      uploadStatus as DataLoaderUploadStatuses
    )
  ) {
    return false;
  }

  // Use existing shouldPoll helper to check both anti-virus and decryption statuses
  if (shouldPoll(antiVirusStatus, decryptionStatus)) {
    return false; // Still scanning or decrypting
  }

  // APEX-1151: Check if validation is still processing
  if (isValidationEnabled && validationStatus === 'PROCESSING') {
    return false; // Still validating
  }

  return true; // Ready to stop loading
};

export type PollingMode = "none" | "single" | "dual";

/**
 * APEX-1151: Helper function to determine if validation has reached a terminal state.
 * Extracts this check to follow Single Responsibility Principle.
 */
const isValidationTerminal = (validationStatus: string): boolean => {
  return (
    validationStatus === ValidationStatuses.SUCCESS ||
    validationStatus === ValidationStatuses.FAILED
  );
};

export const getPollingMode = (
  isFetchingRequestDetailsLoading: boolean,
  dataFilePayroll: { data?: any } | undefined,
  isValidationEnabled: boolean,
  validationStatus?: string
): PollingMode => {
  if (isFetchingRequestDetailsLoading) return "none";
  if (!dataFilePayroll) return "none";

  const status = dataFilePayroll?.data?.status as
    | DataLoaderUploadStatuses
    | undefined;

  const isProcessing =
    status === DataLoaderUploadStatuses.PROCESSING ||
    status === DataLoaderUploadStatuses.UPLOADED;

  if (!isProcessing) return "none";

  // APEX-1151: Stop polling if validation reached terminal state (SUCCESS or FAILED)
  if (isValidationEnabled && validationStatus && isValidationTerminal(validationStatus)) {
    return "none"; // Stop polling
  }

  return isValidationEnabled ? "dual" : "single";
};

export const stopRequestPollingWhenReady = (
  canReloadRequestDetails: boolean,
  intervalRef: React.MutableRefObject<any>
) => {
  if (canReloadRequestDetails && intervalRef?.current) {
    clearTimeout(intervalRef.current);
  }
};

type PollingHandlers = {
  intervalRef: React.MutableRefObject<any>;
  refreshRequestDetails: () => void | Promise<any>;
  refreshValidationResults?: () => void | Promise<any>;
};

export const applyPollingMode = (
  pollingMode: PollingMode,
  { intervalRef, refreshRequestDetails, refreshValidationResults }: PollingHandlers
): PollingMode => {
  if (pollingMode === "dual") {
    if (refreshValidationResults) {
      resetDualPollingInterval(
        intervalRef,
        refreshRequestDetails,
        refreshValidationResults
      );
    }
    return pollingMode;
  }

  if (pollingMode === "single") {
    resetRequestDetailsInterval(intervalRef, refreshRequestDetails);
    return pollingMode;
  }

  return "none";
};

export const cleanupPaymentIntervals = (
  ...intervalRefs: React.MutableRefObject<any>[]
) => {
  intervalRefs.forEach((ref) => {
    if (ref?.current) {
      clearTimeout(ref.current);
    }
  });
};

type PaymentPollingOptions = {
  isFetchingRequestDetailsLoading: boolean;
  dataFilePayroll: { data?: any } | undefined;
  isValidationEnabled: boolean;
  validationStatus?: string;
  intervalRef: React.MutableRefObject<any>;
  refreshRequestDetails: () => void | Promise<any>;
  refreshValidationResults?: () => void | Promise<any>;
};

export const usePaymentPollingController = ({
  isFetchingRequestDetailsLoading,
  dataFilePayroll,
  isValidationEnabled,
  validationStatus,
  intervalRef,
  refreshRequestDetails,
  refreshValidationResults,
}: PaymentPollingOptions): void => {
  useEffect(() => {
    const pollingMode = getPollingMode(
      isFetchingRequestDetailsLoading,
      dataFilePayroll,
      isValidationEnabled,
      validationStatus
    );

    applyPollingMode(pollingMode, {
      intervalRef,
      refreshRequestDetails,
      refreshValidationResults,
    });
  }, [isFetchingRequestDetailsLoading, dataFilePayroll, isValidationEnabled, validationStatus]);
};

type RemovePayrollFileOptions = {
  key: PaymentKey;
  intervalRef: React.MutableRefObject<any>;
  uploadFileData: CreateDocumentResponse | undefined;
  deleteDocument: (params: DeleteDocumentRequest) => void;
  resetFns: {
    resetDocumentDetail?: () => void;
    resetUploadFile: () => void;
    resetUploadPayrollFile?: () => void;
  };
  setDisbursementData: (data: DisbursementDataType[]) => void;
  disbursementData: DisbursementDataType[];
  setPresignPayrollState?: (state: any) => void;
  setCanReloadRequestDetails?: (flag: boolean) => void;
  orgToken?: string;
};

export const removePayrollFileFromChecklist = (
  options: RemovePayrollFileOptions
): void => {
  handleRemoveFile(options);
};

const Payment = (): React.ReactElement => {
  const intervalRef: React.MutableRefObject<any> = useRef(null);
  const intervalPrefundRef: React.MutableRefObject<any> = useRef(null);
  const [canReloadRequestDetails, setCanReloadRequestDetails] = useState(false);

  const [{ errors: presignPayrollError }, setPresignPayrollState] = useState<{
    isLoading?: boolean;
    isError?: boolean;
    errors?: any;
    data?: any;
  }>({});

  const { orgIdWithRel, selectedOrgIdWithRelName, selectedOrgIdWithRelCode, dataGetOrgRel, pspSPC } = useOrgIdWithRel();
  const { isPSPMakerCheckerRole, isPSPMakerRole } = useRoles();

  const history = useHistory();
  const toast = useToast();

  const [
    uploadFile,
    {
      data: uploadFileData,
      reset: resetUploadFile,
      isSuccess: isUploadSuccess,
      isError: isUploadPayrollFileError,
      error: uploadPayrollFileError,
    },
  ] = useCreateDocumentMutation();

  const [
    getDocument,
    {
      data: payrollDocumentDetail,
      isLoading: isPayrollDocumentDetailLoading,
      reset: resetPayrollDocumentDetail,
      error: payrollDocumentDetailError,
    },
  ] = useGetDocumentMutation();

  const [deleteDocument] = useDeleteDocumentMutation();

  const [
    uploadPayrollFile,
    { reset: resetUploadPayrollFile, error: uploadPayrollFileServiceError },
  ] = useUploadDataMutation();

  const { data: profileData } = useGetProfileQuery();
  const membershipOrganisation = getOrganisationMembership(
    profileData?.data?.memberships
  );

  const { data: organizationData } = useGetOrganizationQuery(
    {
      orgId: membershipOrganisation?.organisationId || "",
      userId: profileData?.data?.userId || "",
    },
    { skip: !profileData }
  );
  const serviceProviderCode = !isPSPMakerRole ? organizationData?.data?.companyNumber : pspSPC;
  const businessUnitCode =
    selectedOrgIdWithRelCode || organizationData?.data?.leiNumber;
  const tradingName = useMemo(() => {
    if (!isPSPMakerCheckerRole)
      return organizationData?.data?.tradingName ?? "";

    const selectedOrg = dataGetOrgRel?.data?.find(
      (item) => item.ownerOrg.id === orgIdWithRel
    );

    return selectedOrg?.ownerOrg.tradingName ?? "";
  }, [dataGetOrgRel, isPSPMakerCheckerRole, orgIdWithRel, organizationData]);

  const [requestQuery, setRequestQuery] = useState<FileDetailsRequest>({
    requestId: "",
  });

  const requestId = useMemo(() => requestQuery.requestId, [requestQuery]);

  const {
    data: dataFilePayroll,
    isFetching: isFetchingRequestDetailsLoading,
    refetch: refreshRequestDetails,
    isError: isPayrollError,
    error: payrollError,
  } = useGetRequestDetailsQuery(requestQuery, {
    skip: !requestQuery.requestId,
  });

  // Dual polling for validation results (enterprise customers)
  // Controlled by feature flag - disabled until backend implements proper endpoints
  const isValidationEnabled = env.featureFlags.fileProcessingValidation;

  // APEX-1151: Use inputDocumentId (post-decryption filename) for validation query
  const validationFilename = dataFilePayroll?.data?.inputDocumentId || "";

  // APEX-1151: Extract validation error for handling API errors (results not ready)
  const {
    data: validationResults,
    refetch: refreshValidationResults,
    isError: isValidationError,
  } = useGetValidationResultsQuery(validationFilename, {
    skip: !validationFilename || !isValidationEnabled,
  });

  // APEX-1151: Map API error to InProgress status (results not ready yet, keep polling)
  const validationStatus = useMemo(() => {
    return getValidationStatusWithError(
      validationResults?.validationStatus,
      isValidationError
    );
  }, [validationResults, isValidationError]);

  const [dataFilePayrollLocal, setDataFilePayrollLocal] =
    useState(dataFilePayroll);

  useEffect(() => {
    setDataFilePayrollLocal(dataFilePayroll);
  }, [dataFilePayroll]);

  // APEX-1151: Include validation status in reload check
  useEffect(() => {
    const canReload = shouldEnablePayrollReload(
      payrollDocumentDetail,
      dataFilePayroll,
      validationStatus,
      isValidationEnabled
    );
    setCanReloadRequestDetails(canReload);
    stopRequestPollingWhenReady(canReload, intervalRef);
  }, [dataFilePayroll, isPayrollError, isUploadPayrollFileError, validationStatus, isValidationEnabled]);

  usePaymentPollingController({
    isFetchingRequestDetailsLoading,
    dataFilePayroll,
    isValidationEnabled,
    validationStatus,
    intervalRef,
    refreshRequestDetails,
    refreshValidationResults,
  });

  const [selectedDisbursementDate, setSelectedDisbursementDate] = useState<
    Date | undefined
  >();
  const [paymentInputFiles, setPaymentInputFiles] = useState(undefined);
  const [paymentProofFiles, setPaymentProofFiles] = useState(undefined);
  const onDrop = useCallback((acceptedFiles) => {
    setPaymentInputFiles(acceptedFiles);
  }, []);

  useEffect(() => {
    if (paymentInputFiles) {
      setCanReloadRequestDetails(false);
      onUpload(paymentInputFiles);
    }
  }, [paymentInputFiles]);

  const onDropProof = useCallback((acceptedFiles) => {
    setPaymentProofFiles(acceptedFiles);
  }, []);

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: [
      ".csv, text/csv, application/csv, text/x-csv, application/x-csv, .csv.gpg",
    ].join(", "),
    disabled: false,
  });

  const {
    getRootProps: getProofRootProps,
    getInputProps: getProofInputProps,
    open: openProofInputProps,
    isDragActive: isProofDragActive,
  } = useDropzone({
    onDrop: onDropProof,
    multiple: false,
    accept: [".jpeg", ".jpg", ".png", ".pdf"],
    disabled: false,
  });

  const defaultDisbursementData = [
    {
      key: PaymentKey.UPLOAD_PAYROLL_FILE,
      title: "Upload a payroll file",
      supportText: "Max file size is 5MB. Supported file types are .csv and .csv.gpg.",
      validations: {
        maxFileSize: {
          value: 5 * 1024 * 1024,
          message: "File exceeds size limit",
        },
        fileType: {
          value: [
            ".csv, text/csv, application/csv, text/x-csv, application/x-csv, .csv.gpg",
          ],
          message: "File type is invalid",
        },
      },
      inputProps: getInputProps,
      open: open,
      rootProps: getRootProps,
      isSuccess: false,
      files: [],
    },
    {
      key: PaymentKey.UPLOAD_PROOF_PREFUND,
      title: "Upload proof of prefund",
      supportText:
        "Max file size is 5MB. Supported file types are .jpg, .png and .pdf.",
      validations: {
        maxFileSize: {
          value: 5 * 1024 * 1024,
          message: "File exceeds size limit",
        },
        fileType: {
          value: [".jpeg", ".jpg", ".png", ".pdf"],
          message: "File type is invalid",
        },
      },
      isSuccess: false,
      rootProps: getProofRootProps,
      inputProps: getProofInputProps,
      open: openProofInputProps,
      files: [],
    },
  ];

  useEffect(() => {
    return () => {
      cleanupPaymentIntervals(intervalRef, intervalPrefundRef);
    };
  }, []);

  const [disbursementData, setDisbursementData] = useState<
    DisbursementDataType[]
  >(defaultDisbursementData);

  useEffect(() => {
    if (isUploadSuccess) {
      toast({
        title: "File Uploaded Successfully",
        description: "",
        status: "success",
        duration: 9000,
        position: "top-right",
        isClosable: true,
      });
    }
  }, [isUploadSuccess]);

  const getVerifyDisbursementDate = (disbursementDate?: Date) => {
    if (disbursementDate) {
      if (verifyUploadBeforeTenAMToday(disbursementDate)) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  };

  const onUpload = async (files: File[]) => {
    if (!files?.length) return;

    const [file] = files;
    const fileName = file.name;
    if (!getVerifyDisbursementDate(selectedDisbursementDate)) return;

    const cloned = cloneDeep(disbursementData);
    const idx = cloned.findIndex(
      (c) => c.key === PaymentKey.UPLOAD_PAYROLL_FILE
    );
    const item = cloned[idx];

    const isNameValid = Boolean(
      getVerifyFileName(
        fileName,
        PaymentKey.UPLOAD_PAYROLL_FILE,
        serviceProviderCode,
        businessUnitCode,
        selectedDisbursementDate,
        tradingName
      )
    );
    const isSizeValid = validationFileSize(
      file,
      item.validations.maxFileSize.value
    );

    item.files[0] = { fileName };
    item.isSuccess = isNameValid && isSizeValid;

    if (!isNameValid) {
      const suffix =
        tradingName === PaymentMethod.NORMAL_PAY ? "PREPAID" : "POSTPAID";
      item.files[0].errors = [
        `Uploaded File name is invalid. Correct file name should be GOT_${businessUnitCode}_${suffix}_DISBURSEMENT_YYYYMMDD.\nPlease reupload with the correct file name.`,
      ];
    } else if (!isSizeValid) {
      item.files[0].errors = [item.validations.maxFileSize.message];
    }

    setDisbursementData(cloned);

    if (item.isSuccess) {
      onCallApiUploadPayroll(file);
    }
  };

  const onCallApiUploadPayroll = (file: File) => {
    const reader = new FileReader();

    reader.onload = async () => {
      const fileDetails = {
        orgToken: membershipOrganisation?.token,
        userId: profileData?.data?.userId,
        name: file.name,
        contentType: getMimeType(file.name, file.type),
        orgId: isPSPMakerRole ? orgIdWithRel : undefined,
        meta: {
          keepOriginalFileName: "true",
          folderName: env.api.uploadFolderPath,
          preSignedEnabled: "true",
        },
      };

      const response = (await uploadFile({ ...fileDetails })) as {
        data: CreateDocumentResponse;
      };

      if (response?.data?.data?.meta?.preSignedLink) {
        const presignResponse = await onUploadPresignLink(
          response?.data?.data?.meta?.preSignedLink,
          file,
          file.type,
          PaymentKey.UPLOAD_PAYROLL_FILE
        );

        if (presignResponse && response?.data?.data?.id) {
          getDocument({
            fetchMetaDataOnly: true,
            documentId: response?.data?.data?.id,
            orgToken: membershipOrganisation?.token,
          });
        }
      }
    };

    reader.readAsDataURL(file);
  };

  const onUploadPresignLink = async (
    presignLink: string,
    biranyData: any,
    contentType: string,
    key: PaymentKey
  ) => {
    try {
      const state = {
        isLoading: true,
        isError: false,
        data: undefined,
        errors: undefined,
      };

      if (key === PaymentKey.UPLOAD_PAYROLL_FILE) setPresignPayrollState(state);

      await fetch(presignLink, {
        method: "PUT",
        body: biranyData,
        headers: {
          "Content-Type": contentType,
          "x-amz-acl": "bucket-owner-full-control",
        },
      });

      return true;
    } catch (err: any) {
      const state = {
        isLoading: false,
        isError: true,
        errors: {
          data: { errors: [{ message: err.message }] },
        },
        data: undefined,
      };
      if (key === PaymentKey.UPLOAD_PAYROLL_FILE) {
        setPresignPayrollState(state);
        setCanReloadRequestDetails(true);
      }

      return false;
    }
  };

  const {
    isLoadingPrefund,
    proofPrefundFile,
    disbursementData: hookDisbursementData,
    prefundDocumentDetail,
    hasErrorPrefund,
    errorMessagesPrefund,
    uploadProofFileData,
    handleRemoveFile: handleRemoveFileProof,
  } = useUploadPrefund({ defaultDisbursementData: [defaultDisbursementData[1]], paymentProofFiles, intervalPrefundRef });

  useEffect(() => {
    if (hookDisbursementData) {
      setDisbursementData((prevData) => {
        const updatedData = setNewDisbursementData(prevData, hookDisbursementData);
        return updatedData;
      });
    }
  }, [hookDisbursementData])

  const [
    createApprovalProcess,
    {
      isSuccess: isCreateApprovalProcessSuccess,
      isLoading: isCreateApprovalProcessLoading,
      isError: isCreateApprovalProcessError,
      error: createApprovalProcessError,
    },
  ] = useCreateProcessesMutation();

  useEffect(() => {
    if (isCreateApprovalProcessError) {
      toast({
        title: (createApprovalProcessError as any)?.data?.errors
          ?.map((err: any) => err.message)
          ?.join(", ")
          .replace(Roles.BUChecker, "Business Checker")
          .replace(Roles.BUApprover, "Business Approver"),
        description: "",
        status: "error",
        duration: 9000,
        position: "top-right",
        isClosable: true,
      });
    }
  }, [isCreateApprovalProcessError, createApprovalProcessError]);

  const payrollFile = disbursementData.find(
    (item) => item.key === PaymentKey.UPLOAD_PAYROLL_FILE
  )?.files?.[0];

  const handleSubmit = (): void => {
    const body = {
      orgId: orgIdWithRel,
      orgToken: membershipOrganisation?.token,
      requesterFirstName: profileData?.data?.firstName || "", // User logged in
      requesterLastName: profileData?.data?.lastName || "", // User logged in
      requesterEmail: profileData?.data?.email || "", // User logged in
      referenceNumber: `${payrollFile?.fileName}_${requestId}`, // Payroll file name
      approvalCategoryCode: isPSPMakerCheckerRole
        ? (env.api.approvalCategoryCodePSP || APPROVAL_CATEGORY_CODES.PAYROLL_FILE_SYSTEM_PSP)
        : env.api.approvalCategoryCode,
      approvalObjectId: "9b50d9c8-91dd-49b7-8138-9566f83929be",
      approvalObjectOwnerId: profileData?.data?.userId || "", // User logged in ID
      approvalObjectEntity: "payment",
      processVariables: {
        overallInformation: {
          showFlag: true,
          paymentType: tradingName ?? "",
          amount:
            dataFilePayrollLocal?.data?.metaData?.find(
              ({ key }: { key: string }) => key === "totalAmount"
            )?.value ?? "0",
          payrollDocumentId: dataFilePayroll?.data?.originalDocumentId,
          // NOSONAR - APEX-873: Line within async submit handler; logic tested in getPayrollDocumentName() unit tests (line 2280)
          payrollDocumentName: getPayrollDocumentName(dataFilePayroll), // NOSONAR
          totalItemsCount: String(
            dataFilePayrollLocal?.data?.totalRecord ?? "0"
          ),
          relatedDocumentId: uploadProofFileData?.data?.id, // Proof of fund document ID,
          relatedDocumentName: proofPrefundFile?.fileName ?? "", // Proof of fund document name
          disbursementDate: dateFormat(
            selectedDisbursementDate?.toString(),
            false,
            FORMATS["YYYY/MM/DD"]
          ), //disbursement date
          uploadedDateTime: dataFilePayrollLocal?.data?.createdAt,
        },
        customerInformation: {
          showFlag: true,
          name: organizationData?.data?.companyName ?? "",
          customerId: organizationData?.data?.orgId ?? "",
        },
        action: {
          showFlag: false,
          baseURLSystemUI: env.app.rootUrl,
          actionURL: `${env.app.rootUrl}/payments/${payrollFile?.fileName}`,
        },
      },
    };
    createApprovalProcess(body);
  };

  useEffect(() => {
    if (requestId && isCreateApprovalProcessSuccess) {
      history.push(`/payroll-success/${requestId}`, {
        dataFilePayroll: dataFilePayrollLocal,
        proofPrefundFile,
        scheduleDate: selectedDisbursementDate,
      });
    }
  }, [requestId, isCreateApprovalProcessSuccess]);

  const isValidPayrollFile =
    payrollFile &&
    dataFilePayrollLocal?.data?.status === DataLoaderUploadStatuses.SUCCESS &&
    (!payrollFile?.errors ||
      (payrollFile?.errors && payrollFile?.errors?.length === 0));

  const isValidProffPrefundFile = useMemo(() => {
    return isPrefundFileValid(tradingName, proofPrefundFile, prefundDocumentDetail);
  }, [tradingName, proofPrefundFile, prefundDocumentDetail]);

  // APEX-1151: Check if file validation is valid (only when feature enabled)
  const isValidFileValidation = useMemo(() => {
    if (!isValidationEnabled) return true; // Feature flag disabled → allow
    if (!validationFilename) return true; // No filename → allow
    if (isValidationError) return false; // API error → block (still processing)
    return validationResults?.validationStatus === 'SUCCESS';
  }, [isValidationEnabled, validationFilename, isValidationError, validationResults]);

  const isSubmitLoading = isCreateApprovalProcessLoading;

  // APEX-1151: Include validation check in submission logic
  const isEnableSubmit =
    !isFetchingRequestDetailsLoading &&
    isValidPayrollFile &&
    isValidProffPrefundFile &&
    isValidFileValidation &&
    selectedDisbursementDate &&
    verifyUploadBeforeTenAMToday(selectedDisbursementDate);


  const uploadPayrollFileFunc = useCallback(async () => {
    if (isPayrollDocumentDetailLoading || !payrollDocumentDetail) return;

    const antiVirusStatus = payrollDocumentDetail?.data?.antiVirusStatus;
    const decryptionStatus = payrollDocumentDetail?.data?.decryptionStatus;

    // Continue polling if anti-virus is scanning or decryption is in progress
    if (shouldPoll(antiVirusStatus, decryptionStatus)) {
      resetRequestDocumentInterval(
        intervalRef,
        getDocument,
        uploadFileData,
        membershipOrganisation?.token
      );
      return;
    }

    clearTimeout(intervalRef.current);

    // Check if anti-virus and decryption status are valid
    if (!isFileValid(antiVirusStatus, decryptionStatus)) {
      setCanReloadRequestDetails(true);
      return;
    }

    const orgId = !isPSPMakerRole ? profileData?.data.memberships[0].organisationId : orgIdWithRel;
    const orgToken = isPSPMakerRole ? membershipOrganisation?.token : undefined;

    // Extract documentId explicitly to avoid uncovered branches in optional chaining
    // Use data.name (decrypted filename) with fallback to data.id for backward compatibility
    const documentData = payrollDocumentDetail.data;
    const documentId = documentData ? (documentData.name || documentData.id) : undefined;

    const requestResponse = await uploadPayrollFile({
      uploadType: "DMS",
      datasetName: DataLoaderDataSets.PAYROLL_DS,
      documentId,
      orgId,
      entityId: ENTITY_ID,
      orgToken: membershipOrganisation?.token,
    });

    setRequestQuery({
      requestId: (requestResponse as any)?.data?.data?.requestId,
      orgToken
    });
  }, [payrollDocumentDetail, isPayrollDocumentDetailLoading, uploadFileData]);

  const handleCloseIconClick = (key: PaymentKey) => {
    if (key === PaymentKey.UPLOAD_PAYROLL_FILE) {
      const orgToken = isPSPMakerRole
        ? membershipOrganisation?.token
        : undefined;
      removePayrollFileFromChecklist({
        key,
        intervalRef,
        uploadFileData,
        deleteDocument: (params) => {
          deleteDocument(params);
        },
        resetFns: {
          resetDocumentDetail: resetPayrollDocumentDetail,
          resetUploadFile,
          resetUploadPayrollFile,
        },
        setDisbursementData,
        disbursementData,
        setPresignPayrollState,
        setCanReloadRequestDetails,
        orgToken,
      })

      setDataFilePayrollLocal(undefined);
      return;
    }

    handleRemoveFileProof(
      key,
      intervalPrefundRef
    );
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement("a");
    link.href = TEMPLATE_FILE_URL;
    const suffix =
      tradingName === PaymentMethod.NORMAL_PAY ? "PREPAID" : "POSTPAID";
    const fileName = `GOT_${businessUnitCode}_${suffix}_DISBURSEMENT_YYYYMMDD.csv`;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  useEffect(() => {
    uploadPayrollFileFunc();
  }, [uploadPayrollFileFunc]);

  useEffect(() => {
    // Reset disbursement UI state
    setDisbursementData(defaultDisbursementData)
    setSelectedDisbursementDate(undefined)
    setRequestQuery({
      requestId: "",
    })

    // Reset document upload and API states
    resetUploadFile()
    resetPayrollDocumentDetail()
    resetUploadPayrollFile()

    // Reset error and loading states
    setPresignPayrollState({})
    setCanReloadRequestDetails(false)

    // Clear any running intervals
    if (intervalRef.current) clearTimeout(intervalRef.current)
    if (intervalPrefundRef.current) clearTimeout(intervalPrefundRef.current)
  }, [selectedOrgIdWithRelName])

  return (
    <Flex
      className="Payment"
      w="100%"
      h="100%"
      p="20px"
      pb="64px"
      flexDir={"column"}
      position="relative"
      minHeight="calc(100vh - 124px)"
    >
      <Text textStyle={"h1"}>Upload</Text>
      <Flex pos={[null, null, "relative"]} mt={"10px"} w="100%">
        <Box my="24px">
          <SimpleGrid columns={[1, 2, 3]} spacing={["10px", null, "20px"]}>
            {disbursementData.map((child) => {
              const isHasFiles = child?.files && child?.files?.length > 0;
              const isDragging =
                child.key === PaymentKey.UPLOAD_PAYROLL_FILE
                  ? isDragActive
                  : isProofDragActive;

              return (
                <Flex key={child.key} flexDir="column">
                  <Divider
                    h="1px"
                    w="100%"
                    bgColor={isHasFiles ? "blue.500" : "#E7E7E7"}
                  />
                  <Flex align="center">
                    <Flex
                      justify="center"
                      align="center"
                      borderWidth={2}
                      borderStyle="solid"
                      borderColor={isHasFiles ? "blue.500" : "#e7e7e7"}
                      mr="10px"
                      w="20px"
                      h="20px"
                      borderRadius="50%"
                    >
                      {isHasFiles && (
                        <CheckIcon color="blue.500" fontSize="10px" />
                      )}
                    </Flex>
                    <Text my={"20px"} fontWeight={600}>
                      {child.title}
                    </Text>
                  </Flex>
                  <Text my={"20px"} minH="30px">
                    {child.supportText}
                  </Text>
                  <Flex
                    {...child.rootProps()}
                    key={child.key}
                    textAlign="center"
                    h="135px"
                    flexDir="column"
                    borderRadius="5px"
                    borderWidth={1}
                    borderColor={isDragging ? "blue.500" : "#C6C6C6"}
                    borderStyle="dashed"
                    fontWeight="500"
                    fontSize="12px"
                    justifyContent="center"
                    alignItems="center"
                    onClick={child.open}
                    cursor={isHasFiles ? "not-allowed" : "pointer"}
                  >
                    <Text color={!isHasFiles ? "blue.500" : "#C6C6C6"} p="10px">
                      {isDragging
                        ? "Drop the files here ..."
                        : "Drag and drop files here or click to upload"}
                    </Text>
                    <input
                      {...child.inputProps()}
                      disabled={isHasFiles}
                      data-testid={`${child.key}-input-upload`}
                    />
                  </Flex>
                  {child?.files?.map((file) => (
                    <Flex
                      key={file.fileName}
                      flexDir="column"
                      border={
                        file?.errors ||
                          (child.key === PaymentKey.UPLOAD_PAYROLL_FILE &&
                            payrollDocumentDetail?.data?.antiVirusStatus ===
                            AntiVirusStatuses.INFECTED) ||
                          (child.key === PaymentKey.UPLOAD_PROOF_PREFUND &&
                            prefundDocumentDetail?.data?.antiVirusStatus ===
                            AntiVirusStatuses.INFECTED)
                          ? "1px solid #DA1E28"
                          : ""
                      }
                      mt={"10px"}
                    >
                      <Flex
                        bgColor={"#F4F4F4"}
                        height="48px"
                        alignItems="center"
                        justifyContent="space-between"
                        px="16px"
                        data-testid={`${child.key}-filename-uploaded`}
                      >
                        <Text fontSize={"10px"} noOfLines={1}>
                          {file.fileName}
                        </Text>
                        <CloseIcon
                          data-testid={`${child.key}-close-icon`}
                          cursor="pointer"
                          onClick={() => {
                            handleCloseIconClick(child.key);
                          }}
                        />
                      </Flex>
                      <Divider
                        h="1px"
                        w="100%"
                        bgColor="#E7E7E7"
                        borderColor={"initial"}
                        display={!file?.errors ? "none" : "block"}
                      />
                      {child?.key === PaymentKey.UPLOAD_PAYROLL_FILE &&
                        (!file?.errors || file?.errors?.length === 0) &&
                        <PayrollAdditionalData
                          dataFilePayrollLocal={dataFilePayrollLocal}
                          payrollError={payrollError}
                          uploadPayrollFileError={uploadPayrollFileError}
                          payrollDocumentDetailError={payrollDocumentDetailError}
                          presignPayrollError={presignPayrollError}
                          uploadPayrollFileServiceError={uploadPayrollFileServiceError}
                          canReloadRequestDetails={canReloadRequestDetails}
                          payrollDocumentDetail={payrollDocumentDetail}
                          validationResults={validationResults}
                          isValidationEnabled={isValidationEnabled}
                          isValidationError={isValidationError} />}

                      {child?.key === PaymentKey.UPLOAD_PROOF_PREFUND &&
                        (!file?.errors || file?.errors?.length === 0) &&
                        <AdditionalData
                          hasError={hasErrorPrefund}
                          isLoading={isLoadingPrefund}
                          errorMessages={errorMessagesPrefund}
                          doc={prefundDocumentDetail}
                        />}

                      {file?.errors &&
                        file?.errors.map((error) => (
                          <Flex
                            key={error}
                            bgColor={"#F4F4F4"}
                            alignItems="center"
                            data-testid={`${child.key}-error-messages`}
                            w="100%"
                          >
                            <Text
                              color={"red"}
                              px="16px"
                              py="8px"
                              whiteSpace="pre-wrap"
                              maxW="100%"
                              wordBreak="break-word"
                            >
                              {error}
                            </Text>
                          </Flex>
                        ))}
                    </Flex>
                  ))}
                </Flex>
              );
            })}
            <Box>
              <Divider
                h="1px"
                w="100%"
                bgColor={!!selectedDisbursementDate ? "blue.500" : "#E7E7E7"}
              />
              <Flex align="center">
                <Flex
                  justify="center"
                  align="center"
                  borderWidth={2}
                  borderStyle="solid"
                  borderColor={
                    !!selectedDisbursementDate ? "blue.500" : "#e7e7e7"
                  }
                  mr="10px"
                  w="20px"
                  h="20px"
                  borderRadius="50%"
                >
                  {!!selectedDisbursementDate && (
                    <CheckIcon color="blue.500" fontSize="10px" />
                  )}
                </Flex>

                <Text my={"20px"} fontWeight={600}>
                  Schedule disbursement
                </Text>
              </Flex>
              <Text mt={"20px"} minHeight="43.5px">
                Payments scheduled on a public holiday will be processed on the
                next working day.
              </Text>
              <Flex
                h="125px"
                flexDir="column"
                fontWeight="500"
                fontSize="12px"
                _hover={{ cursor: "pointer" }}
                justifyContent="end"
                flexGrow={1}
                marginTop="15px"
              >
                <Text mb={"10px"}>Calendar</Text>
                <DatePicker
                  data-testid="disbursement-date"
                  onChange={(date) => {
                    setSelectedDisbursementDate(date);
                    if (
                      tradingName === PaymentMethod.NORMAL_PAY &&
                      !proofPrefundFile
                    ) {
                      toast({
                        title: "Proof of Prefund is mandatory",
                        description: "",
                        status: "error",
                        duration: 9000,
                        position: "top-right",
                        isClosable: true,
                      });
                    }
                  }}
                  selected={selectedDisbursementDate}
                  minDate={new Date()}
                  filterDate={() => {
                    // Remove 10 AM restriction - allow all dates
                    return true;
                  }}
                />
              </Flex>
            </Box>
          </SimpleGrid>
          <Flex mt={"20px"}>
            <Text
              _hover={{ cursor: "pointer" }}
              fontSize={"16px"}
              color="blue.500"
              textDecoration={"underline"}
              onClick={handleDownloadTemplate}
            >
              Download template
            </Text>
            <ArrowForwardIcon ml={"10px"} color="blue.500" fontSize={"16px"} />
          </Flex>
        </Box>
      </Flex>
      <Button
        data-testid="submit-button"
        variant="solid"
        colorScheme="blue"
        borderRadius="10px 0 10px 0"
        w={["100%", "300px"]}
        textAlign={"left"}
        rightIcon={<ArrowForwardIcon color="white" />}
        onClick={handleSubmit}
        isLoading={isSubmitLoading}
        disabled={!isEnableSubmit || isSubmitLoading}
        position="absolute"
        bottom={0}
        right={0}
      >
        Submit
      </Button>
    </Flex>
  );
};

export default Payment;
